import { useEffect, useState } from "react"

export type List<T> = T[]
export type ListItem<T> = T

interface FilteredListProps<Res, T> {
  serviceCall?: () => Promise<Res>
  adapter?: (data: Res) => List<T>
  listToFilter?: List<T>
  label?: keyof T
}

/**
 * Custom hook for filtering a list of items in FilteredList.tsx component.
 *
 * @template Res - The type of the response from the service call.
 * @template T - The type of the items in the list.
 *
 * @param {FilteredListProps<Res, T>} props - The props for the filtered list.
 * @param {() => Promise<Res>} props.serviceCall - The function that makes the service call to fetch the data.
 * @param {(data: Res) => List<T>} props.adapter - The function that adapts the response data to the list of items.
 * @param {List<T>} props.listToFilter - The initial list of items to filter.
 * @param {string} props.label - The label property to use for filtering.
 *
 * @returns {{
 *   filteredList: List<T>,
 *   itemsSelected: List<T>,
 *   searchItem: string,
 *   setItemsSelected: (items: List<T>) => void,
 *   onSearchItem: (value: string) => void,
 *   label: string
 * }} - The filtered list and related functions and state.
 */
export const useFilteredList = <Res, T>({
  serviceCall,
  adapter,
  listToFilter,
  label,
}: FilteredListProps<Res, T>) => {
  const [itemList, setItemList] = useState<List<T>>()
  const [searchItem, setSearchItem] = useState<string>()
  const [filteredList, setFilteredList] = useState<List<T>>()
  const [itemsSelected, setItemsSelected] = useState<List<T>>()

  const onSearchItem = (value: string) => {
    setSearchItem(value)
    const newFilteredItemList = itemList?.filter((item: ListItem<T>) => {
      if (typeof item === "string") {
        return item.toLowerCase().includes(value.toLowerCase())
      }
      if (label) {
        const itemLabel =
          typeof item[label] === "string"
            ? (item[label] as string)
            : String(item[label])
        return itemLabel.toLowerCase().includes(value.toLowerCase())
      }
      if (typeof item === "string") {
        return item.toLowerCase().includes(value.toLowerCase())
      }
      return undefined
    })
    setFilteredList(value ? newFilteredItemList : itemList)
  }

  const setfirstRenderData = async () => {
    try {
      if (listToFilter) {
        setItemList(listToFilter)
        setFilteredList(listToFilter)
        return
      }
      if (serviceCall) {
        const data = await serviceCall()
        const adaptedData =
          Array.isArray(data) && !adapter ? data : adapter?.(data) ?? []
        setItemList(adaptedData)
        setFilteredList(adaptedData)
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
    }
  }

  useEffect(() => {
    setfirstRenderData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    filteredList,
    itemsSelected,
    searchItem,
    setItemsSelected,
    onSearchItem,
    label,
  }
}
